import {
  FocusOutEventArgs,
  TextBoxComponent,
} from "@syncfusion/ej2-react-inputs";
import { DialogComponent } from "@syncfusion/ej2-react-popups";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Container, Row } from "react-grid-system";
import Webcam from "react-webcam";
import { verifyExtension } from "../../../utility/pathHelper";

interface ICaptureImageModal {
  open: boolean;
  onAddImage: (folder: string, imageName: string, imageContent: Blob) => void;
  onClose: () => void;
  folder: string;
  initImageName: string;
}

export const CaptureImageModal: FC<ICaptureImageModal> = ({
  open,
  onAddImage,
  onClose,
  folder,
  initImageName,
}) => {
  const [imageName, setImageName] = useState<string>(initImageName);
  const [url, setUrl] = useState<string | null>(null);
  const [imageContent, setImageContent] = useState<Blob>();
  const [errors, setErrors] = useState("");
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | undefined>();

  const handleDevices = (mediaDevices: MediaDeviceInfo[]) => {
    const cameras = mediaDevices.filter(({ kind }) => kind === "videoinput");
    const currentCamera =
      cameras?.find((c) => c.label?.includes("Rear")) ?? cameras?.[0];
    if (currentCamera) {
      setCurrentDeviceId(currentCamera.deviceId);
    }
    setDevices(mediaDevices.filter(({ kind }) => kind === "videoinput"));
  };

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, []);

  const switchCamera = () => {
    if (devices.length > 1) {
      const currentIndex = devices.findIndex(
        (device) => device.deviceId === currentDeviceId
      );
      const nextIndex = (currentIndex + 1) % devices.length;
      setCurrentDeviceId(devices[nextIndex].deviceId);
    }
  };

  const changeHandler = (event: any) => {
    setImageName(event.value);
  };

  let textareaObj: any;

  useEffect(() => {
    validate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageName, imageContent]);

  const buttons = [
    {
      buttonModel: {
        content: "Swap",
        cssClass: "e-flat",
        iconCss: "e-icons e-repeat",
        disabled: devices.length <= 1 || (url?.length ?? 0) > 0,
      },
      click: () => switchCamera(),
    },
    {
      buttonModel: {
        content: url ? "Reset" : "Capture",
        cssClass: "e-flat",
        iconCss: "e-icons e-video",
      },
      click: () => {
        if (url) {
          setUrl(null);
        } else {
          capture();
        }
      },
    },
    {
      buttonModel: {
        content: "Upload",
        cssClass: "e-flat",
        isPrimary: true,
        disabled: errors && errors.length > 0 ? true : false,
      },
      click: () => {
        if (imageName && imageContent) {
          //saveBlob();
          onAddImage(
            `images${folder}`,
            verifyExtension(imageName, "png"),
            imageContent
          );
        }
      },
    },
    {
      buttonModel: {
        content: "Cancel",
        cssClass: "e-flat",
      },
      click: () => {
        onClose();
      },
    },
  ];

  const firstPosition = { X: 400, Y: 0 };

  const validate = () => {
    let error = "";
    if (!imageName) {
      error = "Please enter image name.";
    }

    if (!imageContent) {
      error = error + " Please select image.";
    }

    setErrors(error);
  };

  const videoConstraints = {
    width: 720,
    height: 360,
    facingMode: "user",
  };

  const webcamRef = useRef<Webcam>(null);

  const capture = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot({
      width: 720,
      height: 360,
    });
    if (imageSrc) {
      setUrl(imageSrc);
      const [, imageSrcB64] = imageSrc.split(",", 2);
      const bstr = atob(imageSrcB64);
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) {
        u8arr[i] = bstr.charCodeAt(i);
      }
      const imageBlob = new Blob([u8arr], { type: "image/png" });
      setImageContent(imageBlob);
    }
  }, [webcamRef]);

  return (
    <DialogComponent
      width="600px"
      close={onClose}
      header={`Capture Image ${folder} Part Production Order`}
      visible={open}
      showCloseIcon={true}
      buttons={buttons}
      position={firstPosition}
      isModal
    >
      <div className="dialogContent">
        <Container fluid>
          <Row align="stretch">
            <TextBoxComponent
              ref={(scope : TextBoxComponent) => {
                textareaObj = scope;
              }}
              multiline={false}
              placeholder="Enter image name"
              value={imageName}
              input={changeHandler}
            />
          </Row>

          <Row align="stretch">
            {!url && (
              <Webcam
                audio={false}
                width={550}
                height={300}
                ref={webcamRef}
                screenshotFormat="image/png"
                videoConstraints={{
                  deviceId: currentDeviceId,
                  width: 720,
                  height: 360,
                }}
              />
            )}

            {url && (
              <img
                width={550}
                //height={300}
                src={url}
                alt="Screenshot"
              />
            )}
          </Row>

          <Row align="stretch">
            <h6 style={{ color: "red" }}>{errors}</h6>
          </Row>
        </Container>
      </div>
    </DialogComponent>
  );
};
